import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Save, Clock, Target, Search, Filter, X } from 'lucide-react';
import { ExerciseLibrary } from './ExerciseLibrary';
import { ExerciseList } from './ExerciseList';
import { WorkoutSettings } from './WorkoutSettings';
import { CustomWorkout, Exercise, WorkoutExercise } from '../../../types/exercises';

interface WorkoutBuilderProps {
  onClose: () => void;
  onSave: (workout: CustomWorkout) => void;
}

export function WorkoutBuilder({ onClose, onSave }: WorkoutBuilderProps) {
  const [workoutName, setWorkoutName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedExercises, setSelectedExercises] = useState<WorkoutExercise[]>([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const handleAddExercise = (exercise: Exercise) => {
    const workoutExercise: WorkoutExercise = {
      exerciseId: exercise.id,
      sets: [
        {
          type: 'normal',
          reps: 10,
          weight: 0,
          restAfter: 60
        }
      ]
    };
    setSelectedExercises([...selectedExercises, workoutExercise]);
    setShowLibrary(false);
  };

  const handleSave = () => {
    const workout: CustomWorkout = {
      id: crypto.randomUUID(),
      name: workoutName,
      description,
      exercises: selectedExercises,
      estimatedDuration: calculateDuration(),
      targetStrain: calculateTargetStrain(),
      difficulty: calculateDifficulty(),
      type: determineWorkoutType(),
      tags: [],
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };
    onSave(workout);
  };

  const calculateDuration = () => {
    return selectedExercises.reduce((total, exercise) => {
      const setsDuration = exercise.sets.reduce((setTotal, set) => {
        return setTotal + (set.duration || 0) + set.restAfter;
      }, 0);
      return total + setsDuration;
    }, 0);
  };

  const calculateTargetStrain = () => {
    // Implement strain calculation logic
    return 12.0;
  };

  const calculateDifficulty = () => {
    // Implement difficulty calculation logic
    return 'intermediate' as const;
  };

  const determineWorkoutType = () => {
    // Implement workout type determination logic
    return 'strength' as const;
  };

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] overflow-y-auto pb-24">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background/50 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <input
                type="text"
                value={workoutName}
                onChange={(e) => setWorkoutName(e.target.value)}
                placeholder="Workout Name"
                className="bg-transparent border-none text-xl font-bold focus:outline-none focus:ring-0 placeholder:text-muted-foreground"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSettings(true)}
                className="p-2 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <Target className="w-5 h-5" />
              </button>
              <button
                onClick={handleSave}
                disabled={!workoutName || selectedExercises.length === 0}
                className="theme-button px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Workout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Description */}
          <div className="theme-card p-4">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description for your workout..."
              className="w-full bg-transparent border-none resize-none focus:outline-none focus:ring-0 placeholder:text-muted-foreground"
              rows={2}
            />
          </div>

          {/* Exercise List */}
          <div className="theme-card p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Exercises</h2>
              <button
                onClick={() => setShowLibrary(true)}
                className="theme-button px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Exercise
              </button>
            </div>

            {selectedExercises.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No exercises added yet</p>
                <p className="text-sm">Click "Add Exercise" to start building your workout</p>
              </div>
            ) : (
              <ExerciseList
                exercises={selectedExercises}
                onUpdate={(exercises) => setSelectedExercises(exercises)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Exercise Library Modal */}
      {showLibrary && (
        <ExerciseLibrary
          onClose={() => setShowLibrary(false)}
          onSelect={handleAddExercise}
        />
      )}

      {/* Settings Modal */}
      {showSettings && (
        <WorkoutSettings
          onClose={() => setShowSettings(false)}
          workout={{
            name: workoutName,
            description,
            exercises: selectedExercises,
            estimatedDuration: calculateDuration(),
            targetStrain: calculateTargetStrain(),
            difficulty: calculateDifficulty(),
            type: determineWorkoutType()
          }}
          onUpdate={(settings) => {
            setWorkoutName(settings.name);
            setDescription(settings.description || '');
          }}
        />
      )}
    </div>
  );
}